import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SortState } from "@/hooks/useServerTable";

export type Column<T> = {
  /** Matches the API's sortable-field name when `sortable` is set. */
  key: string;
  /**
   * Usually catalogue text. A `ReactNode` is the escape hatch for a merged column that
   * sorts by more than one key — see the opportunities list's client/opportunity header —
   * which builds its own sort controls and sets `sortable: false` so this renders as-is
   * rather than being wrapped in the single sort button below.
   */
  header: React.ReactNode;
  render: (row: T) => React.ReactNode;
  sortable?: boolean;
  /** Right-align numerics so a column of money is scannable. */
  align?: "left" | "right";
  className?: string;
};

export type DataTableProps<T> = {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  sort?: SortState;
  onToggleSort?: (field: string) => void;
  onRowClick?: (row: T) => void;
  /** Fired on pointer-enter and focus — used to warm a detail's data before it is opened. */
  onRowHover?: (row: T) => void;
  caption?: string;
  /**
   * Edge to edge inside its container: no rounding, and a rule above and below instead of a
   * box around. For a table whose container already *is* the edge — a detail modal's body —
   * where a rounded border draws a second wall a few pixels inside the first one.
   */
  flush?: boolean;
};

/**
 * Presentation only. Sorting writes a URL parameter and the API does the work — the
 * table never holds the full set and never slices it.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  sort,
  onToggleSort,
  onRowClick,
  onRowHover,
  caption,
  flush = false,
}: DataTableProps<T>) {
  return (
    <div
      className={cn(
        "border-border bg-card overflow-x-auto",
        flush ? "border-y" : "rounded-lg border",
      )}
    >
      <table className="w-full text-sm">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead className="bg-muted/50 text-muted-foreground">
          <tr>
            {columns.map((column) => {
              const isSorted = sort?.field === column.key;
              const canSort = Boolean(column.sortable && onToggleSort);

              return (
                <th
                  key={column.key}
                  scope="col"
                  aria-sort={isSorted ? (sort.direction === "asc" ? "ascending" : "descending") : undefined}
                  className={cn(
                    "px-4 py-2 font-medium",
                    column.align === "right" ? "text-right" : "text-left",
                    column.className,
                  )}
                >
                  {canSort ? (
                    <button
                      type="button"
                      onClick={() => onToggleSort?.(column.key)}
                      className="hover:text-foreground inline-flex items-center gap-1"
                    >
                      {column.header}
                      {isSorted ? (
                        sort.direction === "asc" ? (
                          <ArrowUp className="h-3 w-3" aria-hidden />
                        ) : (
                          <ArrowDown className="h-3 w-3" aria-hidden />
                        )
                      ) : (
                        <ChevronsUpDown className="h-3 w-3 opacity-40" aria-hidden />
                      )}
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onPointerEnter={onRowHover ? () => onRowHover(row) : undefined}
              onFocus={onRowHover ? () => onRowHover(row) : undefined}
              // A clickable row needs to be reachable and activatable by keyboard, not
              // just by mouse — the project commits to keyboard parity.
              tabIndex={onRowClick ? 0 : undefined}
              role={onRowClick ? "button" : undefined}
              onKeyDown={
                onRowClick
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
              className={cn(
                "border-border border-t",
                onRowClick && "hover:bg-accent/50 focus-visible:ring-ring cursor-pointer focus-visible:ring-2 focus-visible:outline-none",
              )}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={cn(
                    "px-4 py-2.5",
                    column.align === "right" && "tabular text-right",
                    column.className,
                  )}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
